'use client';

import { useEffect, useState } from 'react';
import { Languages } from 'lucide-react';

const languages = [
  { code: 'EN', name: 'English', flag: '🇺🇸' },
  { code: 'ES', name: 'Español', flag: '🇪🇸' },
  { code: 'FR', name: 'Français', flag: '🇫🇷' },
  { code: 'DE', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'JP', name: '日本語', flag: '🇯🇵' },
  { code: 'CN', name: '中文', flag: '🇨🇳' },
  { code: 'PT', name: 'Português', flag: '🇧🇷' },
  { code: 'KR', name: '한국어', flag: '🇰🇷' }
];

export default function MultiLanguageMockup() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    let cycleInterval: NodeJS.Timeout | null = null;
    let translateTimeout: NodeJS.Timeout | null = null;
    let isMounted = true;

    cycleInterval = setInterval(() => {
      if (!isMounted) return;
      if (translateTimeout) clearTimeout(translateTimeout);
      setIsTranslating(true);
      translateTimeout = setTimeout(() => {
        if (!isMounted) return;
        setActiveIndex((i) => (i + 1) % languages.length);
        setIsTranslating(false);
      }, 400);
    }, 2200);

    return () => {
      isMounted = false;
      if (cycleInterval) clearInterval(cycleInterval);
      if (translateTimeout) clearTimeout(translateTimeout);
    };
  }, []);

  return (
    <div className="w-full h-full bg-white dark:bg-slate-900 rounded-2xl border border-border/50 overflow-hidden shadow-xl">
      <div className="px-4 py-3 bg-gradient-to-r from-orange-500/5 to-amber-500/5 border-b border-border/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md">
            <Languages className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-foreground">
            Multi-language
          </span>
        </div>
        <span className="text-xs text-orange-600 dark:text-orange-400 font-semibold bg-orange-500/10 px-2.5 py-1 rounded-full">
          70+ languages
        </span>
      </div>

      <div className="p-4">
        {/* Active language spotlight */}
        <div
          className={`text-center py-5 mb-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-border/30 transition-all duration-300 ${isTranslating ? 'scale-95 opacity-50' : 'scale-100 opacity-100'}`}
        >
          <span className="text-4xl mb-2 block">
            {languages[activeIndex].flag}
          </span>
          <p className="text-base font-bold text-foreground">
            {languages[activeIndex].name}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {isTranslating ? 'Translating...' : 'Ready to generate'}
          </p>
        </div>

        {/* Language grid */}
        <div className="grid grid-cols-4 gap-1.5">
          {languages.map((lang, i) => (
            <div
              key={lang.code}
              className={`py-2 px-1 rounded-lg text-center transition-all duration-300 ${
                i === activeIndex
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md scale-105'
                  : 'bg-slate-50 dark:bg-slate-800/50 text-muted-foreground hover:bg-muted'
              }`}
            >
              <span className="text-base block">{lang.flag}</span>
              <span className="text-[10px] font-medium">{lang.code}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
